import { FC, useMemo, useCallback } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { SimpleGuard } from 'interfaces/guards'
import { AdvisorPublicPagePreset } from 'components/features/coaching/advisorPublicPage/AdvisorPublicPagePreset/AdvisorPublicPagePreset'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { GetPublicAdvisorAction, GetPublicAdvisorReviewsAction } from 'components/features/coaching/advisorStore/actions/publicAdvisorActions'
import { AdvisorHeader } from 'components/features/coaching/advisorPublicPage/AdvisorHeader/AdvisorHeader'
import { createAdvisorSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { Advisor, AdvisorUsername } from 'components/features/coaching/advisor/types/advisor'
import { AdvisorCertificate } from 'components/features/coaching/advisorPublicPage/AdvisorCertificate/AdvisorCertificate'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import styles from 'components/features/coaching/advisorPublicPage/AdvisorPublicPage/AdvisorPublicPage.styl'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { AdvisorRatingBlock } from 'components/features/coaching/advisorPublicPage/AdvisorRatingBlock/AdvisorRatingBlock'
import find from 'lodash/find'
import { AdvisorReviewsBlock } from 'components/features/coaching/advisorPublicPage/AdvisorReviewsBlock/AdvisorReviewsBlock'
import { ReadMore } from 'components/atoms/ReadMore/ReadMore'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'
import { PageAnchorHandler } from 'handlers/PageAnchorHandler/PageAnchorHandler'
import { COACH_PAGE } from 'constants/pages'

type AdvisorPublicPageProps =
  & RouteComponentProps<{ username: AdvisorUsername }>
  & {}

const publicAdvisorGuard: SimpleGuard<AdvisorPublicPageProps['match']['params']> = {
  name: `Advisor Public Page Guard`,
  checker: (state, { username }) => {
    const { advisors } = state.data
    const uid = advisors.usernameToUid[username]
    const advisor = advisors.advisors.byKey[uid]
    const rating_breakdown = advisors.ratingBreakdown[uid]
    return !!advisor && (!!rating_breakdown || rating_breakdown === null)
  },
  loader: (dispatch, { username }) => {
    if (!username) {
      return
    }

    dispatch(new GetPublicAdvisorAction({ username }))
  },
}

const publicAdvisorReviewsGuard: SimpleGuard<AdvisorPublicPageProps['match']['params']> = {
  name: `Advisor Public Page Reviews Guard`,
  checker: (state, { username }) => {
    const { advisors } = state.data
    return !!find(advisors.reviewsPagination, { advisor_username: username })
  },
  loader: (dispatch, { username }) => {
    if (!username) {
      return
    }

    dispatch(new GetPublicAdvisorReviewsAction({ advisor_username: username }))
  },
}

export const AdvisorPublicPage: FC<AdvisorPublicPageProps> = (props) => {
  const { match } = props
  const username = match.params.username.toLowerCase()
  const withUsername = { username }
  const isLoaded = useSimpleGuard(publicAdvisorGuard, withUsername, 'AdvisorPublicPage')
  useSimpleGuard(publicAdvisorReviewsGuard, withUsername, 'AdvisorPublicPage')

  const uid = useStoreSelector((state) => state.data.advisors.usernameToUid[username] ?? null)
  const withAdvisorUid = useMemo(() => ({ uid }), [uid])
  const advisorSelector = useMemo(createAdvisorSelector, [])
  const advisor = useStoreSelector((state) => advisorSelector(state, withAdvisorUid))

  return (
    <>
      { advisor && (
        <AdvisorPublicPageInner advisor={ advisor } />
      )}
      { !isLoaded && (
        <LoadingOverlay />
      )}
    </>
  )
}


type AdvisorPublicPageInnerProps = {
  advisor: Advisor
}
const AdvisorPublicPageInner: FC<AdvisorPublicPageInnerProps> = (props) => {
  const { advisor } = props

  const dispatch = useStoreDispatch()
  const handleCta = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('advisor', {
      mode: 'request',
      step: 'topic',
      fields: {
        advisor_uid: advisor.uid,
        flow: 'profile',
      },
    }))
  }, [dispatch, advisor.uid])

  const headerChild = useMemo(() => {
    return (
      <AdvisorHeader advisor={ advisor } />
    )
  }, [advisor])

  const certificatesChild = useMemo(() => {
    const items = advisor.certificates
      .map((certificate, index) => (
        <CardBlock
          key={ index }
          corners='all'
          forceRoundedCorners
          shadowTheme='soft'
          className={ styles.certificate }
          noBorders
        >
          <AdvisorCertificate certificate={ certificate } />
        </CardBlock>
      ))
      .filter(Boolean)

    if (!items.length) {
      return null
    }

    return items
  }, [advisor.certificates])

  const ratingChild = useMemo(() => {
    return (
      <AdvisorRatingBlock uid={ advisor.uid } />
    )
  }, [advisor.uid])

  const reviewsChild = useMemo(() => (
    <AdvisorReviewsBlock advisor={ advisor } onScheduleCall={ handleCta } />
  ), [advisor, handleCta])

  const bioChild = useMemo(() => {
    if (!advisor.bio) {
      return null
    }
    return (
      <ReadMore
        wideLimit={ 270 }
        narrowLimit={ 90 }
        readMoreChild='Read More'
        text={ advisor.bio }
      />
    )
  }, [advisor.bio])

  return (
    <PageAnchorHandler placement={{ anchor: COACH_PAGE, id: advisor.username }}>
      <PageMeta
        title={ `${ advisor.name } Reviews | ${ advisor.company } | Status Money` }
        description='Video chat with a financial advisor. Get financial advice from the community for free.'
      />
      <AdvisorPublicPagePreset
        header={ headerChild }
        introduction={ bioChild }
        rating={ ratingChild }
        reviews={ reviewsChild }
        certifications={ certificatesChild }
        onCTA={ handleCta }
      />
    </PageAnchorHandler>
  )
}
